/**
 * TanStack Query hooks for /api/v1/validation-rules/.
 *
 * The S3 engine is gated by the `registration_rules` feature flag on the
 * backend; endpoints 404 when disabled. Callers should feature-gate the
 * UI with <FeatureGate flag="registration_rules"> before mounting hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import type { PaginatedResponse } from '@/shared/types/api'

export type Severity = 'BLOCK' | 'WARN' | 'INFO'
export type ValidationRuleScope =
  | 'registration_step'
  | 'entity_registration'
  | 'transfer_initiation'

export interface ValidationRule {
  uuid: string
  name: string
  description: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active: boolean
  priority: number
  scope: ValidationRuleScope
  entity_type: string | null
  entity_type_key: string | null
  trigger_step: string | null
  created_at: string
  updated_at: string
}

export interface ValidationRuleInput {
  name: string
  description?: string | null
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active?: boolean
  priority?: number
  scope?: ValidationRuleScope
  entity_type?: string | null
  trigger_step?: string | null
}

export interface RuleHit {
  rule_uuid: string
  name: string
  severity: Severity
  message: string
  trigger_step: string | null
  duplicate_matches?: Array<{
    ref: string
    holding_uuid: string
    registration_number?: string | null
    owner_name?: string | null
    owner_email?: string | null
    product_name?: string | null
    product_uuid?: string | null
    consumer_account_uuid?: string | null
    structure_values?: Record<string, unknown>
  }>
}

export interface EvaluateResult {
  step: string
  hits: RuleHit[]
}

const KEY = ['validation-rules'] as const

interface ValidationRuleQueryOptions {
  enabled?: boolean
  scope?: ValidationRuleScope
  entityType?: string | null
}

function validationRuleParams(options?: ValidationRuleQueryOptions) {
  const params: Record<string, string | number> = { page_size: 200 }
  if (options?.scope) params.scope = options.scope
  if (options?.entityType) params.entity_type = options.entityType
  return params
}

export function useValidationRules(options?: ValidationRuleQueryOptions) {
  return useQuery<ValidationRule[]>({
    queryKey: [...KEY, options?.scope ?? 'all', options?.entityType ?? 'all'],
    queryFn: () =>
      api
        .get<PaginatedResponse<ValidationRule>>('/v1/validation-rules/', {
          params: validationRuleParams(options),
        })
        .then((r) => r.data.results),
    staleTime: 30_000,
    enabled: options?.enabled,
  })
}

export function useCreateValidationRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ValidationRuleInput) =>
      api.post('/v1/validation-rules/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateValidationRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: { uuid: string } & Partial<ValidationRuleInput>) =>
      api.patch(`/v1/validation-rules/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteValidationRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/v1/validation-rules/${uuid}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export interface EvaluateRequest {
  step: string
  scope?: ValidationRuleScope
  entity_type?: string | null
  context: {
    fields?: Record<string, unknown>
    entities?: Record<string, Record<string, unknown>>
    aggregates?: Record<string, unknown>
    roles?: string[]
    permissions?: string[]
  }
}

/** Imperative evaluator - call inside a wizard segment transition. */
export async function evaluateValidationRules(
  body: EvaluateRequest,
): Promise<EvaluateResult> {
  const r = await api.post('/v1/validation-rules/evaluate/', body)
  return r.data as EvaluateResult
}
